<div class="pixtral-dialog">
  <button class="close-btn" mat-icon-button (click)="close()" aria-label="Close Pixtral Info">
    <mat-icon>close</mat-icon>
  </button>

  <div class="pixtral-header">
    <img
      src="assets/logos/mistral.svg"
      alt="Pixtral Large 25.02 Logo"
      class="pixtral-logo"
    />
    <h2>Pixtral Large 25.02</h2>
    <p class="subtitle">Multimodal Vision + Language for high-fidelity understanding and extraction</p>
  </div>

  <div class="pixtral-body">
    <p>
      <strong>Pixtral Large 25.02</strong> is a high-capability <strong>vision-language model</strong> built to
      understand images with the same “instruction-following” discipline you expect from a strong text LLM.
      It shines when users want <strong>accurate visual reading</strong> plus <strong>clear structured outputs</strong>:
      screenshots → answers, documents → extracted fields, charts → insights, product photos → descriptions,
      UI mockups → implementation notes, and more.
      <br /><br />
      In production, Pixtral is most valuable when your app needs a model that can reliably do:
      <strong>see → interpret → reason → output</strong> with stable formatting and minimal hallucination.
    </p>

    <ul>
      <li>
        🖼️ <strong>High-Fidelity Image Understanding:</strong>
        Reads scenes, screenshots, diagrams, and dense visuals with strong attention to details like labels,
        layout, icons, and relationships between elements.
      </li>

      <li>
        🔎 <strong>Document & Screenshot Intelligence:</strong>
        Ideal for receipts, invoices, slide screenshots, PDFs-as-images, and UI captures—extracts key fields,
        identifies sections, and explains what matters in plain language.
      </li>

      <li>
        📊 <strong>Charts, Tables & Visual Analytics:</strong>
        Interprets graphs (trendlines, comparisons, outliers), tables, dashboards, and report visuals—
        then produces summaries, bullet insights, and “what this implies” explanations.
      </li>

      <li>
        🧠 <strong>Multimodal Reasoning:</strong>
        Combines image evidence + text instructions for step-by-step problem solving:
        “Use what you see to answer,” “compare these two screenshots,” “find the mismatch,”
        “explain why the UI is inconsistent,” etc.
      </li>

      <li>
        🧾 <strong>Structured Outputs for Apps:</strong>
        Strong at returning predictable formats—checklists, schemas, labeled fields, and JSON-style structures
        for downstream workflows (routing, validation, storage, automation).
      </li>

      <li>
        ✍️ <strong>Content Generation Grounded in Visuals:</strong>
        Generates captions, alt-text, product descriptions, marketing variations, or creative copy
        that’s anchored to what’s actually present in the image (useful for asset pipelines).
      </li>

      <li>
        ⚡ <strong>Fast, Production-Friendly Throughput:</strong>
        Tuned to be responsive in real applications—great for user-facing “upload → instant explanation”
        flows and iterative prompt loops.
      </li>
    </ul>

    <div class="optimal">
      <h3>Who & when to use Pixtral Large</h3>
      <ul>
        <li>
          <strong>Product teams:</strong> UI screenshot analysis, UX consistency checks, “what changed” comparisons,
          and generating implementation notes from designs.
        </li>
        <li>
          <strong>Business ops:</strong> Document intake—extracting names, totals, dates, line items, policy sections,
          and compliance-relevant fields from files and screenshots.
        </li>
        <li>
          <strong>Data & analytics:</strong> Converting dashboards into plain-English insights, highlighting anomalies,
          summarizing charts for exec updates, and creating “next step” actions.
        </li>
        <li>
          <strong>Marketing & media:</strong> Large-scale asset tagging, captions, alt text, and visual QA
          (“does this creative match brand rules?”).
        </li>
        <li>
          <strong>Developers:</strong> Building multimodal features—visual search helpers, document parsers,
          screenshot explainers, image-grounded copilots, and support tooling.
        </li>
      </ul>
    </div>

    <div class="optimal">
      <h3>What Pixtral Large is best at</h3>
      <ul>
        <li><strong>Image Q&A:</strong> “What does this show?” “Where is the error?” “Which option is selected?”</li>
        <li><strong>Extraction:</strong> receipts/invoices/forms → fields, line items, totals, and normalized output</li>
        <li><strong>Comparison:</strong> two screenshots → differences, regressions, and what to fix</li>
        <li><strong>Visual summarization:</strong> dashboards → trends, key takeaways, risks, and action items</li>
        <li><strong>Grounded writing:</strong> captions/alt text/product copy based on actual visible details</li>
      </ul>
    </div>

    <div class="tips">
      <span>
        Tip: For best results, ask for a <strong>specific output format</strong> (bullets, table, or JSON fields) and
        tell Pixtral what to prioritize (e.g., “errors”, “totals”, “differences”, or “action items”).
      </span>
    </div>
  </div>
</div>
